import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-80 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c0876558ff02e9cf81ba85e2188ab240.jpg"
            alt="ClearPath Consulting Team"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About ClearPath Consulting</h1>
          <p className="text-xl md:text-2xl leading-relaxed">
            Empowering organizations worldwide with expert ServiceNow solutions and strategic consulting services
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-10 rounded-lg">
              <div className="w-16 h-16 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-3xl text-slate-800"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-white">Our Mission</h2>
              <p className="text-lg text-white/90 leading-relaxed">
                To deliver exceptional ServiceNow solutions that transform business operations, enhance efficiency, and drive sustainable growth for organizations across the globe.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-10 rounded-lg">
              <div className="w-16 h-16 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-slate-800"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-white">Our Vision</h2>
              <p className="text-lg text-white/90 leading-relaxed">
                To be the most trusted global partner for ServiceNow implementations, recognized for our innovation, expertise, and commitment to client success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-6 bg-sky-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Founded with a vision to revolutionize how organizations leverage ServiceNow, ClearPath Consulting has grown from a small team of passionate consultants to a global powerhouse with offices across North America and Europe.
                </p>
                <p>
                  Our journey began with a simple belief: that technology should empower people, not complicate their work. This philosophy has guided every project, every implementation, and every client relationship we've built.
                </p>
                <p>
                  Today, we serve clients ranging from Fortune 500 companies to innovative startups, delivering tailored ServiceNow solutions that drive real business value. Our team of certified experts brings decades of combined experience, ensuring that every project is executed with precision and excellence.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20professional%20business%20consultants%20wearing%20conference%20badges%20and%20business%20attire%20at%20ServiceNow%20Knowledge%20Conference%202025%20standing%20together%20in%20modern%20convention%20center%20with%20ServiceNow%20branding%20and%20digital%20displays%20in%20background%2C%20team%20photo%20of%20ClearPath%20Consulting%20employees%20smiling%20and%20networking%20at%20enterprise%20technology%20conference%20event%20with%20bright%20lighting%20and%20contemporary%20corporate%20atmosphere&width=600&height=700&seq=about-story-knowledge25&orientation=portrait"
                alt="ClearPath Consulting Team at ServiceNow Knowledge 2025"
                className="w-full h-full rounded-lg shadow-2xl object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Core Values</h2>
            <p className="text-xl text-gray-600">The principles that guide everything we do</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Integrity</h3>
              <p className="text-white/90 leading-relaxed">
                We operate with transparency and honesty, building trust through ethical practices and delivering on our commitments every time.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Innovation</h3>
              <p className="text-white/90 leading-relaxed">
                We embrace cutting-edge technologies and creative solutions, constantly pushing boundaries to deliver exceptional results.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Collaboration</h3>
              <p className="text-white/90 leading-relaxed">
                We work as partners with our clients, fostering open communication and teamwork to achieve shared success.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-star-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Excellence</h3>
              <p className="text-white/90 leading-relaxed">
                We strive for perfection in every project, maintaining the highest standards of quality and professionalism.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Client Focus</h3>
              <p className="text-white/90 leading-relaxed">
                Your success is our priority. We listen, understand, and deliver solutions tailored to your unique needs.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-sky-400 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-global-line text-2xl text-slate-800"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Global Reach</h3>
              <p className="text-white/90 leading-relaxed">
                With offices worldwide, we bring local expertise and global perspective to every engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20cybersecurity%20network%20visualization%20with%20ServiceNow%20platform%20elements%20showing%20connected%20nodes%20digital%20security%20shields%20data%20flow%20patterns%20and%20cloud%20infrastructure%20in%20light%20blue%20and%20cyan%20tones%20with%20soft%20gradient%20background%20representing%20enterprise%20IT%20security%20operations%20and%20digital%20transformation&width=1920&height=800&seq=global-presence-cyber&orientation=landscape"
            alt="Cybersecurity Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-sky-100/85"></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Global Presence</h2>
            <p className="text-xl text-gray-700">Serving clients across continents with local expertise</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2 text-black">Dallas, TX</h3>
              <p className="text-gray-700">North America HQ</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2 text-black">Calgary, AB</h3>
              <p className="text-gray-700">Canadian Operations</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2 text-black">London, UK</h3>
              <p className="text-gray-700">European Hub</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-4xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-2 text-black">Bangalore, IN</h3>
              <p className="text-gray-700">Asian Hub</p>
            </div>
          </div>
        </div>
      </section>

      {/* World Map Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/4800c67a-dac0-40e4-b0cc-b5da0995f2a0.jpg"
              alt="ClearPath Global Presence Map"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-slate-800 mb-2">500+</div>
              <p className="text-xl text-gray-700">Projects Completed</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-slate-800 mb-2">200+</div>
              <p className="text-xl text-gray-700">Global Clients</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-slate-800 mb-2">50+</div>
              <p className="text-xl text-gray-700">Expert Consultants</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-slate-800 mb-2">98%</div>
              <p className="text-xl text-gray-700">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Ready to Transform Your Business?</h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            Let's discuss how ClearPath Consulting can help you achieve your ServiceNow goals
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="mailto:info@clearpathconsulting-global.com"
              className="px-8 py-4 bg-slate-700 text-white text-lg font-semibold rounded-lg hover:bg-slate-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </a>
            <a
              href="/services"
              className="px-8 py-4 bg-white text-slate-700 text-lg font-semibold rounded-lg border-2 border-slate-700 hover:bg-slate-50 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
